// EmptyState — "nothing in this queue" (§7 States, README "Empty").
//
// Translated from prototype/screens-states.jsx `Empty` (neutral tone).
// Purely presentational: it does not know what a "queue" is, does not fetch,
// and does not decide what the next action should be — the caller supplies
// copy and an optional action. The handoff requires every empty state to
// NAME the next action, so `body` and `action` are the caller's job, not an
// afterthought here.
//
// See FilteredEmptyState for the *filters exclude everything* variant — the
// README §7 explicitly calls these out as two different states, not one
// component with a boolean flag.
import React from 'react';
import { Box, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { SvgIconComponent } from '@mui/icons-material';

interface EmptyStateProps {
  /** Short heading, e.g. "Nothing in your queue". */
  title: string;
  /**
   * Body copy. Must name the next action using numbers already fetched
   * (e.g. "Unassigned has 23 waiting") — never trigger a fetch from here.
   */
  body: string;
  /** Defaults to the inbox glyph; pass a different MUI icon where it reads better. */
  icon?: SvgIconComponent;
  /** Buttons/links the caller wants rendered under the copy, e.g. a primary Btn. */
  action?: React.ReactNode;
}

export default function EmptyState({ title, body, icon: Icon = InboxOutlinedIcon, action }: EmptyStateProps) {
  return (
    <Box
      sx={{
        padding: '40px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        textAlign: 'center'
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 'var(--r-md)',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: 'var(--card-sub)',
          color: 'var(--muted)',
          border: '1px solid var(--hairline)'
        }}
      >
        <Icon sx={{ fontSize: 21 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)' }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', marginTop: '5px', lineHeight: 1.6, maxWidth: 340 }}>
          {body}
        </Typography>
      </Box>
      {action ? <Box sx={{ display: 'flex', gap: '8px', marginTop: '2px' }}>{action}</Box> : null}
    </Box>
  );
}
